import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { CheckCircle, Clock, Shield } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Input } from '../../../components/ui/input';
import { COINIS_LOGO_DARK } from '../../../config';
import { authService } from '../../../services/auth';
import { generateErrorMessages } from '../../../utils/showApiErrors';
import { emailSchema } from '../../setup/schemas/email';

// Zod schema for email validation
const forgotPasswordSchema = emailSchema.transform((vals) => ({
  email: vals.email.trim().toLowerCase()
}));

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

type ForgotPasswordStep = 'request' | 'sent';

export const ForgotPassword = ({ onGoBack }: { onGoBack: () => void }): JSX.Element => {
  const [currentStep, setCurrentStep] = useState<ForgotPasswordStep>('request');
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState('');
  const [error, setError] = useState<string | string[] | null>(null);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ''
    },
    mode: 'onTouched'
  });

  const handleSubmit = async (values: ForgotPasswordFormValues): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);
      await authService.requestPasswordReset({ email: values.email });
      setEmailSent(values.email);
      setCurrentStep('sent');
    } catch (e) {
      const axiosError = e as AxiosError;
      setError(generateErrorMessages(axiosError));
    } finally {
      setIsLoading(false);
    }
  };

  if (currentStep === 'sent') {
    return (
      <>
        <img src={COINIS_LOGO_DARK} className="h-7 w-auto" alt="Coinis" />

        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-onboarding-success-light">
            <CheckCircle className="h-5 w-5 text-onboarding-success" />
          </div>
          <h1 className="font-onboarding-1 text-[1.625rem] font-bold leading-[1.1] tracking-[-0.02em] text-primary">
            Check your email
          </h1>
        </div>

        <p className="text-[15px] font-medium leading-[1.5] text-[var(--ink-2)]">
          If <span className="font-semibold text-[var(--ink)]">{emailSent}</span> is registered, you&apos;ll receive a
          password reset link shortly.
        </p>

        <div className="rounded-xl border border-[var(--line)] bg-surface px-4 py-4">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-widest text-[var(--ink-3)]">Next steps</p>
          <ol className="flex flex-col gap-2 text-sm text-[var(--ink)]">
            {[
              'Check your email inbox',
              'Click the secure reset link',
              'Create your new password',
              'Sign in with 2FA verification'
            ].map((step, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-gold-50 text-[10px] font-bold text-brand-orange">
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-brand-gold/30 bg-brand-gold-50 px-4 py-3">
          <Clock className="h-4 w-4 shrink-0 text-brand-gold" />
          <p className="text-sm font-semibold text-brand-orange">Link expires in 15 minutes</p>
        </div>

        <button
          type="button"
          onClick={onGoBack}
          className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          Return to login
        </button>

        <p className="text-center text-sm text-[var(--ink-2)]">
          Didn&apos;t receive it?{' '}
          <button
            type="button"
            onClick={() => setCurrentStep('request')}
            className="cursor-pointer font-semibold text-brand-blue"
          >
            Resend email
          </button>
        </p>

        <p className="text-center text-xs text-[var(--ink-3)]">
          Check your spam folder or contact support if you still don&apos;t see it.
        </p>
      </>
    );
  }

  return (
    <>
      <img src={COINIS_LOGO_DARK} className="h-7 w-auto" alt="Coinis" />
      <div className="flex flex-col gap-[11px]">
        <h1 className="font-onboarding-1 text-[1.625rem] font-bold leading-[1.1] tracking-[-0.02em] text-primary">
          Reset your password
        </h1>
        <p className="text-[15px] leading-[1.5] text-[var(--ink-2)]">
          Enter your email and we&apos;ll send you a secure reset link.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(handleSubmit)(e);
        }}
        className="flex flex-col gap-3.5"
        noValidate
      >
        <div>
          <Input
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="send"
            placeholder="you@yourbrand.com"
            aria-invalid={!!form.formState.errors.email}
            {...form.register('email')}
          />
          {form.formState.errors.email && (
            <p className="mt-1.5 text-xs text-loss">{form.formState.errors.email.message}</p>
          )}
        </div>

        <div className="rounded-lg border border-brand-blue/10 bg-brand-blue-50 p-4">
          <div className="flex items-start gap-3">
            <Shield className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
            <div className="text-sm">
              <p className="mb-1 font-semibold text-brand-blue">Secure reset process</p>
              <ul className="space-y-0.5 font-medium text-[var(--ink-2)]">
                <li>• Reset link expires in 15 minutes</li>
                <li>• Single-use token for security</li>
                <li>• 2FA will remain enabled</li>
              </ul>
            </div>
          </div>
        </div>

        {error && (
          <div className="text-loss text-sm font-medium">
            {Array.isArray(error) ? error.map((err, i) => <div key={i}>{err}</div>) : error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-cta-orange mt-2 flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isLoading ? (
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Sending Reset Email...</span>
            </div>
          ) : (
            'Send Reset Email'
          )}
        </button>
      </form>

      <p className="mt-2 text-center text-sm text-[var(--ink-2)]">
        Remember your password?{' '}
        <button type="button" onClick={onGoBack} className="cursor-pointer font-semibold text-brand-blue">
          Sign in
        </button>
      </p>
    </>
  );
};
